import { z } from 'zod';
import { optionalPhoneNumberValidation, firstNameValidation, lastNameValidation, requiredPhoneNumberValidation, professionalDegreeValidation, websiteLinkValidation, streetAddressValidation, stateValidation, cityValidation, zipCodeValidation, emailValidation, uploadLogoValidation, sessionFeeRange } from './validations';

const regexPattern = /^[A-Za-z0-9.,!''/\"&:+()-\s]*$/;
const professionalHeadlineValidation = z
  .string()
  .optional()
  .superRefine((value, ctx) => {
    if (!value || value.length === 0) {
      return;
    }
    if (value.length > 0 && value.length < 2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Professional Headline must be at least 2 characters long.',
      });
    }
    if (value.length > 100) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Professional Headline must be less than 100 characters long.',
      });
    }
    else if (!regexPattern.test(value)) {
      ctx.addIssue({
        code: 'custom',
        message: "Professional Headline can only contain letters, numbers, spaces, and . , ! ' ' / \" & : + ( ) - characters.",
      });
    }
  })


export const profileBasicInfoSchema = z.object({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  headline: professionalHeadlineValidation,
  professional_degree_id: professionalDegreeValidation
});

export const accountSettingsSchema = z.object({
  first_name: firstNameValidation,
  last_name: lastNameValidation,
  phone_number: requiredPhoneNumberValidation,
  email: emailValidation,
});

export const currentPracticeSchema = z.object({
  current_status: z.enum([
    "accepting_new_clients",
    "not_accepting_clients",
  ], {
    errorMap: () => ({ message: "Please select any one option to continue." }),
  }),
});


export const aboutMeValidation = z.object({
  about_me: z.string()
    .regex(/^[A-Za-z0-9.,!''/\"&:+()-\s]*$/, {
      message: "Bio can only contain letters, numbers, spaces, and . , ! ' ' / \" & : + ( ) - characters."
    })
    .nullable()
    .transform((val) => val.replace(/\n/g, '').trim())
    .superRefine((value, ctx) => {
      if (value === null || value.length === 0) {
        return;
      }
      if (value.length > 0 && value.length < 5) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Bio must be at least 5 characters long.',
        });
      }
      if (value.length > 2000) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Bio must be less than 2000 characters long.',
        });
      }
    })
})

export const contactValidation = z.object({
  phone_number: requiredPhoneNumberValidation,
  // business_number: optionalPhoneNumberValidation
})

export const additionalOfficeValidation = z.object({
  fax_number: optionalPhoneNumberValidation,
  office_number: optionalPhoneNumberValidation
})

export const profileLinksValidation = z.object({
  website_link: websiteLinkValidation,
  facebook: websiteLinkValidation,
  // twitter: websiteLinkValidation,
  linkedin: websiteLinkValidation
});

export const profileAddressValidation = z.object({
  address1: streetAddressValidation,
  state: stateValidation,
  city: cityValidation,
  zip_code: zipCodeValidation
})

const credentialsName = z
  .enum(["membership", "license", "pre-license"], {
    errorMap: () => ({ message: "Please select a credential type." }),
  });

const credentialsNumber = z
  .string()
  .trim()
  .min(1, 'Please add an ID or number.')
  .max(20, "ID or number must be less than 20 characters long.")
  .regex(/^[A-Za-z0-9\s-]+$/, "ID or number can only contain letters, numbers, or special character -");

const currentYear = new Date().getFullYear();
const credentialsYear = z
  .string()
  .trim()
  .nonempty("Please add a year.")
  .regex(/^\d{4}$/, "Please enter a valid year (YYYY).")
  .refine(
    (val) => {
      const year = parseInt(val, 10);
      return year >= 1900 && year <= currentYear;
    },
    {
      message: `Please enter a year between 1900 and ${currentYear}.`,
    }
  );

export const profileCredentialsSchema = z.object({
  name: credentialsName,
  licenseType: z.coerce.number().nullable().optional(),
  state: z.coerce.number().nullable().optional(),
  number: z.string().trim().optional(),
  year: z.string().trim().optional(),
  title: z.string().trim().optional(),
  supervisorName: z.string().trim().optional(),
  supervisorLicenseType: z.coerce.number().nullable().optional(),
  supervisorState: z.coerce.number().nullable().optional()
}).superRefine((data, ctx) => {
  const name = data.name;
  
  // License validation
  if (name === 'license') {
    if (!data.licenseType) {
      ctx.addIssue({
        path: ["licenseType"],
        message: "Please select a license type.",
      });
    }
    if (!data.title || data.title === '') {
      ctx.addIssue({
        path: ["title"],
        message: "Please add a licensing board",
      });
    }
    if (!data.state) {
      ctx.addIssue({
        path: ["state"],
        message: "Please select a license state.",
      });
    }
    if (!data.number || data.number === '') {
      ctx.addIssue({
        path: ["number"],
        message: "Please add a license number.",
      });
    }
    if (!data.year || data.year === '') {
      ctx.addIssue({
        path: ["year"],
        message: "Please add a license year.",
      });
    }
    
    // Format validations for license fields
    if (data.title && data.title.length > 0) {
      if (data.title.length < 2) {
        ctx.addIssue({
          path: ["title"],
          message: "Licensing board must be at least 2 characters long.",
        });
      }
      if (data.title.length > 100) {
        ctx.addIssue({
          path: ["title"],
          message: "Licensing board must be less than 100 characters long.",
        });
      }
      if (!/^[A-Za-z' .-]+$/.test(data.title)) {
        ctx.addIssue({
          path: ["title"],
          message: "Licensing board can only contain letters or special characters ' - .",
        });
      }
    }
    
    if (data.number && data.number.length > 0) {
      if (data.number.length > 20) {
        ctx.addIssue({
          path: ["number"],
          message: "ID or number must be less than 20 characters long.",
        });
      }
      if (!/^[A-Za-z0-9\s-]+$/.test(data.number)) {
        ctx.addIssue({
          path: ["number"],
          message: "ID or number can only contain letters, numbers, or special character -",
        });
      }
    }
    
    if (data.year && data.year.length > 0) {
      if (!/^\d{4}$/.test(data.year)) {
        ctx.addIssue({
          path: ["year"],
          message: "Please enter a valid year (YYYY).",
        });
      } else {
        const year = parseInt(data.year, 10);
        const currentYear = new Date().getFullYear();
        if (year < 1900 || year > currentYear) {
          ctx.addIssue({
            path: ["year"],
            message: `Please enter a year between 1900 and ${currentYear}.`,
          });
        }
      }
    }
  }
  
  // Pre-licensure validation
  if (name === 'pre-license') {
    if (!data.supervisorName || data.supervisorName.trim() === '') {
      ctx.addIssue({
        path: ["supervisorName"],
        message: "Please add supervisor's name.",
      });
    }
    if (!data.supervisorLicenseType) {
      ctx.addIssue({
        path: ["supervisorLicenseType"],
        message: "Please select supervisor's license type.",
      });
    }
    if (!data.supervisorState) {
      ctx.addIssue({
        path: ["supervisorState"],
        message: "Please select supervisor's license state.",
      });
    }
    
    // Format validations for pre-licensure fields
    if (data.supervisorName && data.supervisorName.length > 0) {
      if (data.supervisorName.length < 2) {
        ctx.addIssue({
          path: ["supervisorName"],
          message: "Supervisor's name must be at least 2 characters long.",
        });
      }
      if (data.supervisorName.length > 100) {
        ctx.addIssue({
          path: ["supervisorName"],
          message: "Supervisor's name must be less than 100 characters long.",
        });
      }
      if (!/^[A-Za-z' -]+$/.test(data.supervisorName)) {
        ctx.addIssue({
          path: ["supervisorName"],
          message: "Supervisor's name can only contain letters or special characters ' and -",
        });
      }
    }
  }
  
  // Membership validation
  if (name === 'membership') {
    if (!data.title || data.title === '') {
      ctx.addIssue({
        path: ["title"],
        message: "Please add a membership organization",
      });
    }
    // if (!data.number || data.number === '') {
    //   ctx.addIssue({
    //     path: ["number"],
    //     message: "Please add a membership number.",
    //   });
    // }
    if (!data.year || data.year === '') {
      ctx.addIssue({
        path: ["year"],
        message: "Please add a membership year.",
      });
    }
    
    // Format validations for membership fields
    if (data.title && data.title.length > 0) {
      if (data.title.length < 2) {
        ctx.addIssue({
          path: ["title"],
          message: "Membership organization must be at least 2 characters long.",
        });
      }
      if (data.title.length > 100) {
        ctx.addIssue({
          path: ["title"],
          message: "Membership organization must be less than 100 characters long.",
        });
      }
      if (!/^[A-Za-z' .-]+$/.test(data.title)) {
        ctx.addIssue({
          path: ["title"],
          message: "Membership organization can only contain letters or special characters ' - .",
        });
      }
    }
    
    if (data.number && data.number.length > 0) {
      if (data.number.length > 20) {
        ctx.addIssue({
          path: ["number"],
          message: "ID or number must be less than 20 characters long.",
        });
      }
      if (!/^[A-Za-z0-9\s-]+$/.test(data.number)) {
        ctx.addIssue({
          path: ["number"],
          message: "ID or number can only contain letters, numbers, or special character -",
        });
      }
    }
    
    if (data.year && data.year.length > 0) {
      if (!/^\d{4}$/.test(data.year)) {
        ctx.addIssue({
          path: ["year"],
          message: "Please enter a valid year (YYYY).",
        });
      } else {
        const year = parseInt(data.year, 10);
        const currentYear = new Date().getFullYear();
        if (year < 1900 || year > currentYear) {
          ctx.addIssue({
            path: ["year"],
            message: `Please enter a year between 1900 and ${currentYear}.`,
          });
        }
      }
    }
  }
});

const trainingTypes = z
  .enum(['certificate', 'fellowship', 'post-doctoral', 'residency'], {
    errorMap: () => ({ message: "Please select a training type." }),
  });

const institutionNameSchema = z
  .string()
  .trim()
  .min(1, { message: 'Please add an institution/organization name.' })
  .min(2, { message: 'Institution/Organization name must be at least 2 characters long.' })
  .max(100, { message: 'Institution/Organization name must be less than 100 characters long.' })
  .regex(/^[A-Za-z' .-]+$/, {
    message: "Institution/Organization name can only contain letters or special characters ' - .",
  });

const focusSchema = z
  .string()
  .trim()
  .min(1, { message: 'Please add a training focus.' })
  .min(2, { message: 'Training focus must be at least 2 characters long.' })
  .max(100, { message: 'Training focus must be less than 100 characters long.' })
  .regex(/^[A-Za-z' .-]+$/, {
    message: "Training focus can only contain letters or special characters ' - .",
  });

export const profileTrainingSchema = z.object({
  type: trainingTypes,
  type_id: z.coerce.number().nullable().optional(),
  name: institutionNameSchema,
  focus: focusSchema,
  year: credentialsYear,
}).superRefine((data, ctx) => {
  if (data.type === 'certificate') {
    if (!data.type_id) {
      ctx.addIssue({
        path: ["type_id"],
        message: "Please select a certificate type.",
      });
    }
  }
});

const facilityNameSchema = z
  .string()
  .trim()
  .min(1, { message: "Please add a name." })
  .min(2, { message: "Name must be at least 2 characters long." })
  .max(50, { message: "Name must be less than 50 characters long." })
  .regex(regexPattern, {
    message: "Name can only contain letters, numbers, spaces, and . , ! ' ' / \" & : + ( ) - characters.",
  });


export const basicTreatmentProgramSchema = z.object({
  facility_name: facilityNameSchema,
  // business_number: requiredPhoneNumberValidation,
  website_link: websiteLinkValidation,
  facility_type_ids: z.array(z.number().int())
    .nonempty("Please select a treatment facility type."),
  centralized_email: emailValidation.optional().or(z.literal(''))
});

export const treatmentProgramAddressSchema = z.object({
  address1: streetAddressValidation,
  state: stateValidation,
  city: cityValidation,
  zip_code: zipCodeValidation,
  phone_number: requiredPhoneNumberValidation,
  fax_number: optionalPhoneNumberValidation
});


export const profileAvailableServicesSchema = z.object({
  available_services: z.array(z.number().int())
    .nonempty(`Please select at least one service.`),
  detox_for: z.array(z.number().int()),
  payment_options: z.array(z.number().int())
    .nonempty(`Please select at least one payment option.`),
});

const faqValidation = z.string()
  .regex(/^[A-Za-z0-9.,!'\-\s]*$/, {
    message: "FAQ can only contain letters, numbers, spaces, and . , ! ' - characters.",
  })
  .nullable()
  .superRefine((value, ctx) => {
    if (value === null || value.length === 0) {
      return;
    }
    if (value.length > 0 && value.length < 5) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FAQ must be at least 5 characters long.',
      });
    }
    if (value.length > 1000) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'FAQ must be less than 1000 characters long.',
      });
    }
  })


export const profileFaqSchema = z.object({
  question_1: faqValidation,
  question_2: faqValidation,
  question_3: faqValidation,
  question_4: faqValidation,
  question_5: faqValidation,
})

export const profileTeamInvitationSchema = z.object({
  is_email_exists: z.boolean(),
  role: z.enum([
    "team_admin",
    "team_assistant",
    "mental_health_provider",
  ], {
    errorMap: () => ({ message: "Please select a role." }),
  }),
  invite_as_mental_health: z.boolean().optional(),
  emails: z.array(emailValidation).min(1, 'Please add a team member email.'),
});

export const profileImageValidationSchema = z.object({
  main_image: uploadLogoValidation
});

export const sessionFeeSchema = z.object({
  Individuals: sessionFeeRange,
  Couples: sessionFeeRange,
  Families: sessionFeeRange,
  Groups: sessionFeeRange,
  "Clinical Assessment": sessionFeeRange,
  Testing: sessionFeeRange,
  Consultation: sessionFeeRange,
  Supervision: sessionFeeRange,
})